<script setup>
import { computed } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMapGetter } from 'dashboard/composables/store';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const {
  inbox,
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
} = useInbox();

const isWameInbox = computed(() => {
  const attrs =
    inbox.value?.additionalAttributes ||
    inbox.value?.additional_attributes ||
    inbox.value?.channel?.additionalAttributes ||
    inbox.value?.channel?.additional_attributes ||
    {};

  return (
    isAPIInbox.value &&
    (attrs.aceleresOutboundMode ?? attrs.aceleres_outbound_mode) ===
      'webhook' &&
    Boolean(attrs.aceleresInstanceId ?? attrs.aceleres_instance_id)
  );
});

const {
  status,
  id,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
} = useMessageContext();

const uiSettings = useMapGetter('getUISettings');
const isStarred = computed(() => {
  const starredIds = uiSettings.value?.starred_message_ids || [];
  return starredIds.some(messageId => Number(messageId) === Number(id.value));
});

const readableTime = computed(() => messageTimestamp(createdAt.value, 'HH:mm'));

// Aceleres: badge "editada" ao lado do horário quando o content foi editado
// (marcado por `content_attributes.edited` no patch_content do backend).
const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // WAME starts the Chatwoot message as `sent`, before the provider returns
  // its external id. Until then, show progress instead of a false tick.
  if (isWameInbox.value) {
    return !!sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // API inbox messages use real sent/delivered/read status values from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.SENT;

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  if (isWameInbox.value) {
    return !!sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }

  // API inbox messages use real delivered status from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.DELIVERED;
  // All messages marked as delivered for the web widget inbox once they are sent.
  if (isAWebWidgetInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isWameInbox.value) {
    return !!sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});
</script>

<template>
  <div class="flex items-center gap-1 text-[11px] leading-4">
    <!-- Aceleres: a badge "editada" ficava aqui, mas o meta é absoluto no
         canto do bubble e ela vazava pra fora quando a mensagem era curta.
         Agora ela é renderizada dentro do próprio conteúdo (ver Text/Index.vue),
         então flui com o texto e o bubble cresce naturalmente. -->
    <div class="inline">
      <time class="inline">{{ readableTime }}</time>
    </div>
    <Icon
      v-if="isStarred"
      icon="i-lucide-star"
      class="size-3 fill-current"
      :aria-label="$t('CONVERSATION.CONTEXT_MENU.STARRED')"
    />
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
</template>
